import React, { useState, useEffect } from 'react';
import {
    Grid,
    Avatar,
    Box,
    Paper,
    List,
    ListItemButton,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    Toolbar,
    Divider,
    Card,
    CardContent,
    InputBase,
    IconButton,
    TextField,
    Chip
} from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { EmailRounded, PhoneRounded, ContentCopyOutlined, KeyboardBackspaceRounded } from "@mui/icons-material";
import LinkedInImage from "../images/linkedin_3536505.png";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { myCandidatesSelector } from "./redux/selector";
import { useSelector, useDispatch } from "react-redux";
import Key from "../../clientVariables.json";
import VerifiedIcon from '@mui/icons-material/Verified';
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LoadingSkeleton from '../loadingSkeletons/LoadingList';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import IntrestedJobs from './CandidatesTabs/InterestedJobs';

const RecruiterView = () => {
    const {
        jobDetails,
        jobDetailsLoading,
        selectedJob,
        selectedSubmission,
        candidateDetailLoading,
        candidateJobsLoading,
        candidateInterestedJobsLoading,
        activeCandidates,
        activeCandidateTab,
        candidateJobs,
        candidateInterestedJobs,
    } = useSelector(myCandidatesSelector);
    const [isSearching, setIsSearching] = useState(false); // search flag
    const [searchQuery, setSearchQuery] = useState(""); // search input value
    const [selectedContact, setSelectedContact] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [offset, setOffset] = useState(0);
    const [selectedItems, setSelectedItems] = useState([]); // To store selected items
    const [selectedRecruitersData, setSelectedRecruitersData] = useState([]); // To store selected recruiters
    const [message, setMessage] = useState(""); // State for message input
    const [copied, setCopied] = useState(false);
    const maxEmailLength = 20; // Maximum email display length
    const [contactsLoading, setContactsLoading] = useState(false)

    const empMobile = jobDetails?.contact_phone ? jobDetails.contact_phone : jobDetails?.contact_no;

    const isDesktopSmall = useMediaQuery("(min-width: 1200px) and (max-width: 1439px)");
    const isDesktopMedium = useMediaQuery("(min-width: 1440px) and (max-width: 1919px)");
    const isDesktopLarge = useMediaQuery("(min-width: 1920px)");

    const handleCopy = () => {
        if (jobDetails?.email_id) {
            navigator.clipboard.writeText(jobDetails.email_id);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
        }
    };
    const handleCopyMail = () => {
        navigator.clipboard.writeText(selectedContact?.EMP_CONTACT_EMAIL);
    };

    const handleSearchClick = () => {
        setIsSearching(true);
    };

    const handleCloseClick = () => {
        setIsSearching(false);
        setSearchQuery("");
    };

    const handleContactClick = (contact) => {
        //console.log("Contact clicked:", contact); // Debug log
        setSelectedContact(contact); // Set selected contact
    };

    const handleBackClick = () => {
        setSelectedContact(null); // Return to the contact list
    };

    const sendMessage = () => {
        setMessage("");
    };

    const paperStyles = {
        borderRadius: "8px",
        overflow: "hidden",
        height: "calc(100vh - 160px)",
        display: "flex",
        flexDirection: "column",
    };

    const getApolloContacts = () => {
        fetch("https://apps.jobsnprofiles.com/contacts/insert_apollo_contacts", {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                domain_name: selectedJob?.email_id?.split("@")[1],
                company_id: jobDetails?.company_id,
            }),
        })
            .then((response) => response.json())
            .then((resdata) => {
                if (resdata.success === 1) {
                    // setContacts(resdata?.data?.apolloContacts)
                } else {
                    console.error("Unexpected response data:", resdata);
                }
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            });
    };

    // Helper to deduplicate contacts by email (or name if no email)
    const deduplicateContacts = (contactsArr) => {
        const seen = new Set();
        return contactsArr.filter((contact) => {
            const key = contact.EMP_CONTACT_EMAIL || contact.EMP_CONTACT_NAME;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    const fetchCompanyContacts = async () => {
        setSelectedItems([]);
        setSelectedRecruitersData([]);
        setContactsLoading(true);

        try {
            if (!jobDetails?.company_id) {
                setContacts([]);
                return;
            }

            const res = await fetch(`${Key.domain_crm}/company/get_Company_Contacts`, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    page: offset,
                    company_id: jobDetails.company_id,
                }),
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const resdata = await res.json();

            if (resdata?.success === 1) {
                setContacts(deduplicateContacts(resdata?.data?.companyContacts || []));
            } else {
                console.error("Unexpected response data:", resdata);
                setContacts([]); // optional: clear on unexpected payload
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setContactsLoading(false);
        }
    };

    useEffect(() => {
        if (!jobDetails || !jobDetails.company_id || jobDetails?.company_id === "0") {
            //console.log("No company ID available");
            setSelectedContact(null);
            setContacts([]);
            return;
        }
        if (jobDetails?.company_id) {
            //console.log("Fetching contacts for company ID:", jobDetails.company_id);
            setSelectedContact(null); // Clear previous contacts
            fetchCompanyContacts();
        }
        // eslint-disable-next-line
    }, [jobDetails?.company_id, offset]); // Only refetch when company_id or offset changes

    // Filter contacts based on search query
    const filteredContacts = contacts.filter((contact) =>
        contact.EMP_CONTACT_NAME.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getDisplayEmail = (email) =>
        email?.length > maxEmailLength ? `${email.slice(0, maxEmailLength)}...` : email


    const TRUTHY_CONTACT_VALUES = [1, true, '1']

    const hasSelectedSubmission =
        Boolean(selectedSubmission?.submission_id || selectedSubmission?.id || selectedSubmission?.job_id);

    const isJobsTab = activeCandidateTab === 0 || activeCandidateTab === "jobstab";
    const isInterestedTab = activeCandidateTab === 1;
    const isSubmissionTab = activeCandidateTab === 2;

    const hasDataForCurrentTab = () => {
        if (isInterestedTab) {
            return candidateInterestedJobs?.length > 0;
        }
        if (isSubmissionTab) {
            return hasSelectedSubmission;
        }
        return candidateJobs?.length > 0;
    };

    const isPanelLoading = (() => {
        if (isInterestedTab) {
            return (
                candidateDetailLoading ||
                candidateInterestedJobsLoading ||
                (candidateInterestedJobs?.length > 0 && jobDetailsLoading)
            );
        }
        if (isSubmissionTab) {
            return (
                candidateDetailLoading ||
                (hasSelectedSubmission && jobDetailsLoading)
            );
        }
        return candidateDetailLoading || candidateJobsLoading || jobDetailsLoading;
    })();

    return (
        <Paper elevation={2} sx={paperStyles}>
            <Toolbar
                sx={{
                    minHeight: "48px !important",
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                    bgcolor: "#fff",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 500,
                        fontSize: isDesktopSmall ? "0.80rem" : "1rem",
                        margin: 0,
                    }}
                    paragraph
                    id="tableTitle"
                    component="div"
                >
                    {(jobDetails?.is_direct_contact === 1 && jobDetails?.is_direct_email === 1) || (jobDetails?.is_direct_contact === 1 && jobDetails?.is_direct_email === 0) || (jobDetails?.is_direct_contact === 0 && jobDetails?.is_direct_email === 1) ? 'Meet Hiring Team' : 'Recruiter Details'}
                </Typography>
                {jobDetails?.is_direct_contact === 0 && jobDetails?.is_direct_email === 0 &&
                    <Tooltip
                        title="Recruiter details are shown based on available hiring information from the company."
                        placement="top"
                        arrow
                    >
                        <InfoOutlinedIcon
                            sx={{
                                fontSize: 16,
                                color: "#757575",
                                cursor: "pointer",
                                "&:hover": { color: "#1a73e8" },
                            }}
                        />
                    </Tooltip>
                }
            </Toolbar>
            <Divider />
            {activeCandidates.length > 0 ? (
                isPanelLoading ? (
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0, p: 1 }}>
                        <Box sx={{ height: 70, mb: 1, overflow: "hidden" }}>
                            <LoadingSkeleton />
                        </Box>
                        <Divider sx={{ mb: 1 }} />
                        <Box sx={{ flex: 1, overflowY: "auto" }}>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <LoadingSkeleton key={i} />
                            ))}
                        </Box>
                    </Box>
                ) : !hasDataForCurrentTab() ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                        <Typography color="text.secondary">No data</Typography>
                    </Box>
                ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
                    <Card sx={{ boxShadow: "none", borderRadius: "8px", padding: 0 }}>
                        <CardContent sx={{ p: 0 }}>
                            {jobDetailsLoading ? (
                                <Box
                                    sx={{
                                        position: 'relative',
                                        height: 70,
                                        overflow: 'hidden',
                                        px: 1,
                                        pt: 2,
                                        '& > *': {
                                            position: 'static !important',
                                            height: '100% !important',
                                            width: '100% !important',
                                        },
                                        pointerEvents: 'none',
                                    }}
                                >
                                    <LoadingSkeleton />
                                </Box>
                            ) : (
                                <Box sx={{ position: "relative", padding: "4px", width: "100%", height: "70px" }}>
                                    <Box sx={{ display: "flex" }}>
                                        <Box
                                            sx={{
                                                borderLeft: "6px solid rgba(64, 104, 130, 0.8)",
                                                borderTop: "2px solid rgba(64, 104, 130, 0.8)",
                                                borderBottom: "2px solid rgba(64, 104, 130, 0.8)",
                                                borderRadius: "50%",
                                                position: "absolute",
                                                top: "10px",
                                            }}
                                        >
                                            <Box sx={{ borderLeft: "4px solid #fff", borderTop: "1px solid #fff", borderBottom: "1px solid #fff", borderRadius: "50%" }}>
                                                <Avatar sx={{ height: 60, width: 60 }}>
                                                    {/* {jobDetails[0]?.contact_name?.charAt(0)} */}
                                                </Avatar>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {jobDetails?.email_id && <Box sx={{ position: "absolute", right: "2px", top: "10px" }}>
                                        <Typography variant="button" paragraph style={{ textTransform: "none", color: "rgba(0,0,0,0.7)", fontSize: "12px", fontWeight: "bold" }}>
                                            <Tooltip title={copied ? "Copied!" : "Copy"}>
                                                <ContentCopyOutlined
                                                    onClick={handleCopy}
                                                    style={{
                                                        float: "right",
                                                        fontSize: "12px",
                                                        marginTop: "0px",
                                                        cursor: "pointer",
                                                        color: copied ? "#1a8cff" : "#406882",
                                                    }}
                                                />
                                            </Tooltip>
                                            <EmailRounded
                                                sx={{
                                                    height: "10px",
                                                    width: "10px",
                                                    mr: "3px",
                                                    color: copied ? "#1a8cff" : "inherit",
                                                }}
                                            />
                                            <Tooltip title={jobDetails?.email_id || "N/A"}>
                                                <span>{getDisplayEmail(jobDetails?.email_id || "N/A")}</span>
                                            </Tooltip>
                                        </Typography>
                                    </Box>}
                                    {empMobile && empMobile !== "N/A" && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                right: "2px",
                                                bottom: "-10px",
                                                ".phone-text": {
                                                    margin: 0,
                                                    fontSize: "11px",
                                                    color: "rgba(0,0,0,0.7)",
                                                    fontWeight: 500,
                                                    cursor: "pointer",
                                                    textTransform: "lowercase",
                                                },
                                                "&:hover": {
                                                    ".phone-text": {
                                                        color: "#406882",
                                                        textDecoration: "underline",
                                                    },
                                                },
                                            }}
                                        >
                                            <Typography
                                                variant="button"
                                                paragraph
                                                className="phone-text"
                                            >
                                                <PhoneRounded
                                                    sx={{ height: "12px", width: "12px", mr: "3px" }}
                                                />
                                                {empMobile}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            position: "absolute",
                                            left: "65px",
                                            right: 0,
                                            top: "28px",
                                            padding: "3px",
                                            borderRadius: "6px",
                                            width: "auto",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Box>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }} />
                                                <Typography variant="body1" style={{ fontWeight: "bold", fontSize: "12px", display: "flex", alignItems: "center" }}>
                                                    {jobDetails?.contact_name}
                                                    {((TRUTHY_CONTACT_VALUES.includes(jobDetails?.contact_verified)) && jobDetails?.email_id) && (
                                                        <VerifiedIcon sx={{ color: "#1a8cff", fontSize: 16, ml: 0.5 }} />
                                                    )}
                                                </Typography>
                                            </Box>
                                            {(jobDetails?.contact_linkedin_url || jobDetails?.recruiter_linkedin_url) &&
                                                <Box>
                                                    <a href={jobDetails?.contact_linkedin_url || jobDetails?.recruiter_linkedin_url} target="_blank" rel="noopener noreferrer">
                                                        <img
                                                            src={LinkedInImage}
                                                            alt="LinkedIn Profile"
                                                            width="22px"
                                                            height="22px"
                                                            style={{ cursor: "pointer" }}
                                                        />
                                                    </a>
                                                </Box>
                                            }
                                        </Box>
                                    </Paper>
                                </Box>)}
                        </CardContent>
                    </Card>
                    <Divider />
                    <Toolbar
                        sx={{
                            minHeight: "48px !important",
                            position: "sticky",
                            top: 0,
                            zIndex: 3,
                            bgcolor: "#fff",
                        }}
                        style={{ padding: "6px" }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                            {!isSearching ? (
                                <>
                                    <Box sx={{ display: "flex", justifyContent: "space-around", alignItems: "center" }}>
                                        {/* <IconButton>
                                            <CachedIcon />
                                        </IconButton> */}
                                        <Typography
                                            sx={{
                                                fontWeight: 500,
                                                fontSize: isDesktopSmall ? "0.8rem" : "1rem",
                                                margin: 0,
                                            }}
                                            paragraph
                                            id="tableTitle"
                                            component="div"
                                        >
                                            Contacts
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        sx={{ p: 0 }}
                                        onClick={handleSearchClick}
                                        aria-label="search"
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                </>
                            ) : (
                                <>
                                    <IconButton onClick={handleCloseClick} sx={{ cursor: "pointer" }}>
                                        <CloseIcon />
                                    </IconButton>
                                    <TextField
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        variant="standard"
                                        placeholder="Search contacts"
                                        InputProps={{
                                            startAdornment: <SearchIcon sx={{ color: "#406882" }} />,
                                        }}
                                        sx={{ width: "100%" }}
                                    />
                                </>
                            )}
                        </Box>
                    </Toolbar>
                    <Divider />
                    {contactsLoading || jobDetailsLoading ? (
                        <LoadingSkeleton />
                    ) : (
                        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                            {!selectedContact ? (
                                filteredContacts.length === 0 ? (
                                    <Typography variant="body1" sx={{ textAlign: "center", marginTop: 2 }}>
                                        No contacts found
                                    </Typography>
                                ) : (
                                    <List>
                                        {filteredContacts.map((contact, i) => (
                                            <ListItemButton
                                                key={i}
                                                onClick={() => handleContactClick(contact)} // Optional click handler
                                                sx={{ display: 'flex', alignItems: 'center', padding: '10px' }}
                                            >
                                                <Avatar sx={{ marginRight: 1, fontSize: "12px" }}>
                                                    {contact.EMP_CONTACT_NAME.charAt(0)}
                                                </Avatar>
                                                <Box>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            fontSize: "12px",
                                                            fontWeight: 600,
                                                            margin: 0,
                                                            width: isDesktopLarge ? "250px" : "150px",
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                        }}
                                                    >
                                                        {contact.EMP_CONTACT_NAME}
                                                        {((TRUTHY_CONTACT_VALUES.includes(contact?.contact_verified)) && (TRUTHY_CONTACT_VALUES.includes(contact?.email_verified) && contact?.EMP_CONTACT_EMAIL)) ? (
                                                            <VerifiedIcon sx={{ color: "#1a8cff", fontSize: 16, ml: 0.5 }} />
                                                        ) : null}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: "12px",
                                                            margin: 0,
                                                            width: isDesktopLarge
                                                                ? "250px"
                                                                : isDesktopMedium
                                                                    ? "200px"
                                                                    : isDesktopSmall
                                                                        ? "150px"
                                                                        : "100px",
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                        }}
                                                    >
                                                        {contact?.EMP_DECL_TITLE}
                                                    </Typography>
                                                </Box>
                                            </ListItemButton>
                                        ))}
                                    </List>
                                )
                            ) : (
                                <Box sx={{ padding: 2 }}>
                                    {/* Centered content */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 0 }}>
                                        <Tooltip title="Back">
                                            <IconButton onClick={handleBackClick}><ArrowBackIcon /></IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Avatar sx={{ width: 72, height: 72, mb: 1.5 }} />

                                        <Typography
                                            variant="h6"
                                            sx={{ textAlign: 'center', mb: 0.5, fontSize: '16px', fontWeight: 800, color: 'rgba(0,0,0,0.85)' }}
                                        >
                                            {selectedContact?.EMP_CONTACT_NAME || 'Unknown'}
                                        </Typography>
                                        {(selectedContact?.EMP_CONTACT_EMAIL
                                            && selectedContact.EMP_CONTACT_EMAIL !== "None"
                                            && selectedContact.EMP_CONTACT_EMAIL !== "Unknown"
                                            && selectedContact.EMP_CONTACT_EMAIL !== ""
                                            && selectedContact.EMP_CONTACT_EMAIL !== "null") && (
                                                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            textAlign: "center",
                                                            mb: 0.5,
                                                            fontSize: "12px",
                                                            color: "rgba(0,0,0,0.85)",
                                                        }}
                                                    >
                                                        {((TRUTHY_CONTACT_VALUES.includes(selectedContact?.contact_verified)) && (TRUTHY_CONTACT_VALUES.includes(selectedContact?.email_verified) && selectedContact?.EMP_CONTACT_EMAIL)) ? (
                                                            <VerifiedIcon sx={{ color: "#1a8cff", fontSize: 16, ml: 0.5 }} />
                                                        ) : null}
                                                        {selectedContact.EMP_CONTACT_EMAIL}
                                                    </Typography>

                                                    <Tooltip title="Copy">
                                                        <IconButton size="small" onClick={handleCopyMail} sx={{ p: 0.5 }}>
                                                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            )}

                                        <Stack direction="row" spacing={1} sx={{ mb: 1 }} justifyContent="center" flexWrap="wrap">
                                            {selectedContact?.EMP_DECL_TITLE && (
                                                <Chip
                                                    label={selectedContact.EMP_DECL_TITLE}
                                                    size="small"
                                                    sx={{ bgcolor: 'grey.100', fontSize: 12, height: 24, borderRadius: 2 }}
                                                />
                                            )}

                                            {((selectedContact?.EMP_CONTACT_CITY && selectedContact?.EMP_CONTACT_CITY !== "null") || (selectedContact?.EMP_CONTACT_COUNTRY && selectedContact?.EMP_CONTACT_COUNTRY !== "null")) && (
                                                <Chip
                                                    icon={<LocationOnOutlinedIcon sx={{ fontSize: 16 }} />}
                                                    label={
                                                        (selectedContact?.EMP_CONTACT_CITY && selectedContact?.EMP_CONTACT_CITY !== "null")
                                                            ? `${selectedContact.EMP_CONTACT_CITY}${(selectedContact?.EMP_CONTACT_COUNTRY && selectedContact?.EMP_CONTACT_COUNTRY !== "null") ? ', ' + selectedContact.EMP_CONTACT_COUNTRY : ''}`
                                                            : (selectedContact?.EMP_CONTACT_COUNTRY && selectedContact?.EMP_CONTACT_COUNTRY !== "null") ? selectedContact.EMP_CONTACT_COUNTRY : ''
                                                    }
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: 12, height: 24, borderRadius: 2 }}
                                                />
                                            )}
                                        </Stack>

                                        <Divider sx={{ width: '100%', my: 1.25 }} />

                                        {/* Icons only (no text). Render only if available */}
                                        <Stack direction="row" spacing={1.5} justifyContent="center">
                                            {(selectedContact?.EMP_CONTACT_EMAIL && selectedContact?.EMP_CONTACT_EMAIL != "None" && selectedContact?.EMP_CONTACT_EMAIL != "Unknown" && selectedContact?.EMP_CONTACT_EMAIL != "" && selectedContact?.EMP_CONTACT_EMAIL != "null") && (
                                                <Tooltip title="Email">
                                                    {/* Use mailto for default client, or swap href for Outlook Web compose */}
                                                    <IconButton
                                                        color="primary"
                                                        size="medium"
                                                        href={`mailto:${selectedContact.EMP_CONTACT_EMAIL}`}
                                                    // href={`https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(selectedContact.EMP_CONTACT_EMAIL)}`}
                                                    >
                                                        <EmailOutlinedIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {selectedContact?.EMP_LINKEDIN_URL && (
                                                <Tooltip title="LinkedIn">
                                                    <IconButton
                                                        color="primary"
                                                        size="medium"
                                                        href={selectedContact.EMP_LINKEDIN_URL}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <LinkedInIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {selectedContact?.EMP_CONTACT_PHONE && (
                                                <Tooltip title="Phone">
                                                    <IconButton
                                                        color="primary"
                                                        size="medium"
                                                        href={`tel:${selectedContact.EMP_CONTACT_PHONE}`}
                                                    >
                                                        <PhoneRounded />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
                )
            ) : (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                    <Typography color="text.secondary">No data</Typography>
                </Box>
            )}
        </Paper>
    );
};

export default RecruiterView;
